// @ts-nocheck

import { useState } from 'react';
import { Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import AtomLineChart, { DataPoint } from '@/components/atoms/LineChart';
import { useSafeEffect } from '@/hooks/useSafeEffect';
import { getCategoryMarketData, getStudyId, getStudyName, getStudyProfile } from '@/store/studies';
import { studies } from '@/store/studies/thunks';
import { transformData } from '@/utils/helpers';
import { AppDispatch } from 'store';
import {
  selectFinancialsData,
  selectFinancialsLoading,
  selectFinancialsError,
} from '@/store/financials/selectors';
import { financials } from '@/store/financials/thunks';

interface CategoryActionsProps {
  id: string;
  name: string;
  open: boolean;
  action: string;
  onClose: () => void;
  segmentName: string;
  categoryName: string;
  year: number;
}

interface CategoryHistoryChartData {
  id: string;
  data: DataPoint[];
  label: string;
  color: string;
}

// Keep the original makeSeries function for history
const makeSeries = (studyName: string, peerStudies: any[], data: any[]) => {
  const colors = ['#ff9800', '#014421', '#673147', '#800020', '#000080', '#004852', '#8B3103'];
  const output = [];
  peerStudies.map((peer, index) => {
    const peerData = data.filter(item => item.study_name === peer.name);
    if (peerData.length === 0) return; // Skip if no data for this peer

    const item = {
      id: peer.name,
      data: transformData(peerData, 'revenue'),
      label: peer.name,
      color: colors[index],
    };
    output.push(item);
  });
  const primaryStudyData = data.filter(item => item.study_name === studyName);
  const primaryStudySeries = {
    id: studyName,
    data: transformData(primaryStudyData, 'revenue'),
    label: studyName,
    color: '#6e4699',
  };
  return [primaryStudySeries, ...output];
};

// Add the new function for financial data transformation
const transformFinancialData = (financials: any[]): DataPoint[] => {
  return financials
    .toSorted((a, b) => {
      // Sort by year first, then by quarter
      if (a.year !== b.year) return a.year - b.year;
      return a.quarter - b.quarter;
    })
    .map(item => ({
      x: new Date(item.year, (item.quarter - 1) * 3, 1),
      y: item.revenue,
    }));
};

// Add helper function to create financial series
const makeFinancialSeries = (financials: any[]) => {
  return [
    {
      id: 'revenue',
      data: transformFinancialData(financials),
      label: 'Revenue',
      color: '#2196f3',
    },
  ];
};

export const CategoryDialogContent: React.FC<CategoryActionsProps> = ({
  id,
  name,
  open,
  action,
  onClose,
  segmentName,
  categoryName,
  year,
}) => {
  //----------- STATE -------------------
  const chartData = useState<CategoryHistoryChartData[]>([]);

  //----------- DISPATCH -----------------
  const dispatch = useDispatch<AppDispatch>();

  //----------- SELECTORS -----------------
  const studyId = useSelector(getStudyId);
  const studyName = useSelector(getStudyName);
  const categoryData = useSelector(getCategoryMarketData);
  const studyProfile = useSelector(getStudyProfile);
  const financialsData = useSelector(selectFinancialsData);

  //----------- HOOKS ----------------------
  useSafeEffect(async () => {
    const fetchData = async () => {
      switch (action) {
        case 'history':
          await dispatch(
            studies.getCategoryMarketData({
              study_name: studyName,
              year: year,
              segment_name: segmentName,
              category_name: categoryName,
              include_peers: false,
            })
          );
          break;

        case 'financials':
          await dispatch(
            financials.getFinancials({
              category_id__eq: id, // Using the category id from props
              year__eq: year, // Using the year from props
            })
          );
          break;

        default:
          break;
      }
    };
    fetchData();
  }, [action, id, dispatch, segmentName, categoryName, studyName, year]);

  // --------- Content ------------
  switch (action) {
    case 'history':
      return (
        <div>
          <h3>History</h3>
          <div style={{ width: '100%', height: '50vh' }}>
            <AtomLineChart
              series={makeSeries(studyName, studyProfile.peer_studies, categoryData)}
            />
          </div>
        </div>
      );

    case 'financials':
      return (
        <div>
          <h3>Category Financials</h3>
          <Typography variant="subtitle1" gutterBottom>
            {name} - Quarterly Revenue
          </Typography>
          <div style={{ width: '100%', height: '50vh' }}>
            <AtomLineChart
              series={makeFinancialSeries(financialsData)}
              formatAsQuarters={true}
              showHorizontalGrid={true}
              showTooltip={true}
              showLegend={true}
              xLabel="Quarter"
              yLabel="Revenue ($)"
            />
          </div>
        </div>
      );

    default:
      return null;
  }
};
